import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";

function mapAuthError(err) {
  const msg = (err?.message ?? "").toLowerCase();
  if (msg.includes("rate limit") || msg.includes("too many") || msg.includes("over_email_send_rate_limit"))
    return "Wysłano zbyt wiele próśb o link logowania. Odczekaj chwilę i spróbuj ponownie.";
  if (msg.includes("invalid email") || msg.includes("unable to validate"))
    return "Podany adres e-mail jest nieprawidłowy.";
  if (msg.includes("signup") && msg.includes("not allowed"))
    return "Rejestracja nie jest dozwolona. Skontaktuj się z administratorem.";
  if (msg.includes("network") || msg.includes("fetch") || msg.includes("failed to fetch"))
    return "Błąd połączenia. Sprawdź połączenie z internetem i spróbuj ponownie.";
  if (msg.includes("user not found") || msg.includes("no user"))
    return "Nie znaleziono konta powiązanego z tym adresem e-mail.";
  return "Nie udało się wysłać linku. Sprawdź adres e-mail i spróbuj ponownie.";
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
}

function LogoMark({ size = "md" }) {
  const dim = size === "lg" ? "w-20 h-20" : "w-16 h-16";
  const text = size === "lg" ? "text-[2.6rem]" : "text-[2.1rem]";
  const radius = size === "lg" ? "rounded-[22px]" : "rounded-[18px]";
  return (
    <div
      className={`relative ${dim} ${radius} bg-gradient-to-br from-[#ecfaf5] via-[#e2f7f1] to-[#f8efe3] border border-white/80 shadow-[0_10px_24px_rgba(15,23,42,0.08)] overflow-hidden`}
    >
      <div className="absolute inset-[5px] rounded-[13px] border border-white/70" />
      <div className="absolute top-1 left-1 w-4 h-4 border-t-2 border-l-2 border-[#7dc8c0] rounded-tl-[5px]" />
      <div className="absolute bottom-1 left-1 w-4 h-4 border-b-2 border-l-2 border-[#7dc8c0] rounded-bl-[6px]" />
      <div className="absolute top-1 right-1 w-4 h-4 border-t-2 border-r-2 border-[#e7c86d] rounded-tr-[6px]" />
      <div className="absolute bottom-1 right-1 w-4 h-4 border-b-2 border-r-2 border-[#e7c86d] rounded-br-[6px]" />
      <div className="absolute left-1 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#7dc8c0] shadow-[0_0_8px_rgba(125,200,192,0.45)]" />
      <div className="absolute right-1 top-[28%] w-1.5 h-1.5 rounded-full bg-[#e7c86d] shadow-[0_0_8px_rgba(231,200,109,0.45)]" />
      <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#e7c86d] shadow-[0_0_8px_rgba(231,200,109,0.45)]" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex items-end leading-none select-none">
          <span
            className={`font-display ${text} font-semibold tracking-[-0.06em]`}
            style={{
              background: "linear-gradient(180deg,#5C7CA3 0%,#83AEC9 55%,#B9D8E9 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            R
          </span>
          <span
            className={`font-display ${text} font-semibold tracking-[-0.08em]`}
            style={{
              background: "linear-gradient(180deg,#F3DA8B 0%,#E7C86D 48%,#C9A44C 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            B
          </span>
        </div>
      </div>
      <div className="absolute inset-x-4 bottom-0 h-5 bg-gradient-to-t from-white/20 to-transparent" />
    </div>
  );
}

function IconMail() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function IconLock() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

function IconCheckCircle() {
  return (
    <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function IconSend() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
  );
}

function InputField({ label, type, value, onChange, placeholder, required, autoComplete, icon: Icon }) {
  return (
    <div>
      <label className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
        {Icon && <span className="text-slate-300"><Icon /></span>}
        {label}
      </label>
      <input
        type={type}
        className="w-full rounded-2xl border border-slate-200 bg-white/90 px-4 py-3.5 text-sm text-slate-700 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={autoComplete}
      />
    </div>
  );
}

function FeatureCard({ icon, title, desc, accent }) {
  return (
    <div className={`rounded-2xl ${accent} p-4`}>
      <div className="mb-2 text-lg">{icon}</div>
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-700">{title}</div>
      <div className="mt-1 text-sm leading-6 text-slate-500">{desc}</div>
    </div>
  );
}

function RememberMeCheckbox({ checked, onChange }) {
  return (
    <label className="flex cursor-pointer items-center gap-3 select-none">
      <div className="relative flex items-center">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="peer sr-only"
        />
        <div className="h-5 w-5 rounded-md border border-slate-200 bg-white/90 transition-all duration-150 peer-checked:border-emerald-400 peer-checked:bg-emerald-500 peer-focus:ring-2 peer-focus:ring-emerald-100" />
        {checked && (
          <svg
            className="pointer-events-none absolute inset-0 m-auto h-3 w-3 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      <div>
        <span className="text-sm font-medium text-slate-700">Zapamiętaj mnie</span>
        <span className="ml-2 text-xs text-slate-400">
          {checked ? "Zostaniesz zalogowany(-a) po ponownym otwarciu przeglądarki" : "Sesja wygaśnie po zamknięciu przeglądarki"}
        </span>
      </div>
    </label>
  );
}

export default function LoginScreen() {
  const [mode, setMode] = useState("parent");
  const [rememberMe, setRememberMe] = useState(true);

  const [parentEmail, setParentEmail] = useState("");
  const [parentLoading, setParentLoading] = useState(false);
  const [parentSent, setParentSent] = useState(false);
  const [parentError, setParentError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState("");

  const { signIn, signInWithMagicLink } = useAuth();
  const navigate = useNavigate();

  async function handleMagicLink(e) {
    e.preventDefault();
    setParentError("");
    const normalizedEmail = parentEmail.trim().toLowerCase();

    if (!isValidEmail(normalizedEmail)) {
      setParentError("Podaj poprawny adres e-mail, np. jan.kowalski@gmail.com");
      return;
    }

    setParentLoading(true);
    try {
      await signInWithMagicLink(normalizedEmail, rememberMe);
      setParentSent(true);
      setCooldown(60);
    } catch (err) {
      console.log("[MagicLink] Supabase error:", err);
      setParentError(mapAuthError(err));
    } finally {
      setParentLoading(false);
    }
  }

  async function handleResend() {
    setParentError("");
    const normalizedEmail = parentEmail.trim().toLowerCase();
    setParentLoading(true);
    try {
      await signInWithMagicLink(normalizedEmail);
      setCooldown(60);
    } catch (err) {
      console.log("[MagicLink] Resend Supabase error:", err);
      setParentError(mapAuthError(err));
    } finally {
      setParentLoading(false);
    }
  }

  async function handleAdminLogin(e) {
    e.preventDefault();
    setAdminError("");
    setAdminLoading(true);
    try {
      await signIn(adminEmail, adminPassword, rememberMe);
      navigate("/dashboard");
    } catch {
      setAdminError("Nieprawidłowy e-mail lub hasło.");
    } finally {
      setAdminLoading(false);
    }
  }

  function switchMode(m) {
    setMode(m);
    setParentError("");
    setAdminError("");
    setParentSent(false);
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#f6fbfa]">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(167,243,208,0.24),_transparent_28%),radial-gradient(circle_at_top_right,_rgba(253,224,171,0.22),_transparent_24%),radial-gradient(circle_at_bottom_left,_rgba(186,230,253,0.18),_transparent_28%),linear-gradient(180deg,#f8fcfb_0%,#eef8f6_100%)]" />
        <div className="absolute -top-20 -left-16 h-56 w-56 rounded-full bg-emerald-200/35 blur-3xl" />
        <div className="absolute top-[12%] right-[8%] h-32 w-32 rounded-full bg-amber-200/30 blur-2xl" />
        <div className="absolute bottom-[8%] right-[6%] h-64 w-64 rounded-full bg-teal-200/20 blur-3xl" />
        <div className="absolute left-[8%] top-[44%] h-[1px] w-[36%] rounded-full bg-emerald-200/70" />
        <div className="absolute left-[10%] top-[47%] h-[1px] w-[28%] rounded-full bg-sky-200/60" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 py-8 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="relative mb-8 overflow-hidden rounded-[34px] border border-white/70 bg-[linear-gradient(135deg,rgba(255,255,255,0.85)_0%,rgba(236,253,245,0.78)_35%,rgba(255,247,237,0.80)_100%)] px-6 py-7 shadow-[0_20px_60px_rgba(15,23,42,0.07)] backdrop-blur-sm sm:px-8 sm:py-9"
        >
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-10 -top-10 h-40 w-40 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute right-[-20px] top-[-10px] h-36 w-36 rounded-full bg-amber-200/35 blur-2xl" />
          </div>

          <div className="relative grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="rounded-2xl bg-white/80 p-1.5 shadow-[0_8px_20px_rgba(15,23,42,0.07)] ring-1 ring-emerald-100/80">
                  <LogoMark size="md" />
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700/80">
                    Skarbnik klasowy
                  </div>
                  <div className="text-xs text-slate-400">Panel finansów klasy</div>
                </div>
              </div>

              <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl lg:text-[2.6rem] lg:leading-tight">
                Przejrzyste finanse{" "}
                <span
                  style={{
                    background: "linear-gradient(135deg,#10b981 0%,#0d9488 50%,#e7c86d 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  klasy
                </span>
              </h1>

              <p className="mt-3 max-w-md text-base leading-7 text-slate-500 sm:text-lg">
                Wszystkie zbiórki, wydatki i rozliczenia w jednym miejscu.
                Prosto, przejrzyście i bez chaosu.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                {["Zbiórki klasowe", "Rozliczenia wydatków", "Planowane płatności"].map((t) => (
                  <span
                    key={t}
                    className="rounded-full border border-emerald-100 bg-white/80 px-3 py-1 text-xs font-medium text-slate-600 shadow-[0_4px_12px_rgba(15,23,42,0.04)]"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="grid grid-cols-2 gap-3">
                <FeatureCard
                  icon="📊"
                  title="Transparentność"
                  desc="Jasny podgląd wpłat, wydatków i salda w każdej chwili."
                  accent="bg-emerald-50/90"
                />
                <FeatureCard
                  icon="🔒"
                  title="Bezpieczeństwo"
                  desc="Dostęp tylko dla uprawnionych — bez haseł dla rodziców."
                  accent="bg-amber-50/90"
                />
                <FeatureCard
                  icon="📋"
                  title="Kontrola"
                  desc="Wszystkie najważniejsze informacje dostępne od razu."
                  accent="bg-teal-50/90"
                />
                <FeatureCard
                  icon="📩"
                  title="Wygoda"
                  desc="Rodzice logują się jednym kliknięciem w e-mailu."
                  accent="bg-sky-50/90"
                />
              </div>
            </div>
          </div>
        </motion.div>

        <div className="flex flex-1 items-center justify-center pb-6">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-md"
          >
            <div className="rounded-[32px] border border-white/70 bg-white/88 p-8 shadow-[0_28px_72px_rgba(15,23,42,0.11)] backdrop-blur-sm">
              <div className="mb-7 flex flex-col items-center text-center">
                <motion.div
                  initial={{ scale: 0.84, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.15, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
                  className="mb-4 rounded-[22px] bg-white/80 p-2 shadow-[0_10px_24px_rgba(15,23,42,0.06)] ring-1 ring-emerald-100/80"
                >
                  <LogoMark size="md" />
                </motion.div>

                <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
                  Zaloguj się
                </h2>
                <p className="mt-1.5 text-sm text-slate-500">
                  Dostęp do panelu rozliczeń klasy
                </p>
              </div>

              <div className="mb-7 flex rounded-2xl border border-slate-100 bg-slate-50/80 p-1">
                <button
                  type="button"
                  onClick={() => switchMode("parent")}
                  className={`flex-1 rounded-xl py-2.5 text-sm font-semibold transition-all duration-200 ${
                    mode === "parent"
                      ? "bg-white text-slate-900 shadow-[0_6px_16px_rgba(15,23,42,0.08)] ring-1 ring-emerald-100/80"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Zaloguj się linkiem
                </button>
                <button
                  type="button"
                  onClick={() => switchMode("admin")}
                  className={`flex-1 rounded-xl py-2.5 text-sm font-semibold transition-all duration-200 ${
                    mode === "admin"
                      ? "bg-white text-slate-900 shadow-[0_6px_16px_rgba(15,23,42,0.08)] ring-1 ring-emerald-100/80"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Zaloguj się hasłem
                </button>
              </div>

              <AnimatePresence mode="wait">
                {mode === "parent" && (
                  <motion.div
                    key="parent"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25 }}
                  >
                    {!parentSent ? (
                      <form onSubmit={handleMagicLink} className="space-y-5">
                        <div className="rounded-2xl border border-emerald-100/80 bg-emerald-50/60 px-4 py-3">
                          <p className="text-sm leading-6 text-slate-600">
                            Podaj adres e-mail. Wyślemy jednorazowy link do logowania —
                            bez konieczności pamiętania hasła.
                          </p>
                        </div>

                        <InputField
                          label="Adres e-mail"
                          type="email"
                          value={parentEmail}
                          onChange={(e) => setParentEmail(e.target.value)}
                          placeholder="twoj@email.pl"
                          required
                          autoComplete="email"
                          icon={IconMail}
                        />

                        <RememberMeCheckbox checked={rememberMe} onChange={setRememberMe} />

                        {parentError && (
                          <motion.div
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                          >
                            {parentError}
                          </motion.div>
                        )}

                        <button
                          type="submit"
                          disabled={parentLoading}
                          className={`flex w-full items-center justify-center gap-2.5 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-3.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(16,185,129,0.22)] transition-all duration-200 ${
                            parentLoading
                              ? "cursor-not-allowed opacity-60"
                              : "hover:-translate-y-0.5 hover:shadow-[0_14px_28px_rgba(16,185,129,0.30)]"
                          }`}
                        >
                          {parentLoading ? (
                            <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                          ) : (
                            <IconSend />
                          )}
                          {parentLoading ? "Wysyłanie..." : "Wyślij link logowania"}
                        </button>
                      </form>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.35 }}
                        className="text-center"
                      >
                        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-500 ring-4 ring-emerald-100">
                          <IconCheckCircle />
                        </div>

                        <h3 className="text-xl font-semibold text-slate-900">
                          Link wysłany!
                        </h3>

                        <p className="mt-2 text-sm leading-6 text-slate-500">
                          Sprawdź skrzynkę pocztową pod adresem{" "}
                          <span className="font-semibold text-slate-700">{parentEmail}</span>.
                          Kliknij w link, żeby się zalogować.
                        </p>

                        <div className="mt-6 rounded-2xl border border-amber-100 bg-amber-50/60 px-4 py-3">
                          <p className="text-xs leading-5 text-amber-700">
                            Nie widzisz wiadomości? Sprawdź folder SPAM lub poczekaj chwilę.
                          </p>
                        </div>

                        {parentError && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                          >
                            {parentError}
                          </motion.div>
                        )}

                        <button
                          type="button"
                          onClick={handleResend}
                          disabled={parentLoading || cooldown > 0}
                          className={`mt-5 flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-[0_6px_16px_rgba(15,23,42,0.06)] transition-all duration-200 ${
                            parentLoading || cooldown > 0
                              ? "cursor-not-allowed opacity-50"
                              : "hover:border-emerald-200 hover:text-emerald-700"
                          }`}
                        >
                          {parentLoading ? (
                            <span className="h-3.5 w-3.5 rounded-full border-2 border-slate-400 border-t-transparent animate-spin" />
                          ) : null}
                          {parentLoading
                            ? "Wysyłanie..."
                            : cooldown > 0
                            ? `Wyślij ponownie za ${cooldown} s`
                            : "Wyślij ponownie"}
                        </button>

                        <button
                          type="button"
                          onClick={() => { setParentSent(false); setParentEmail(""); }}
                          className="mt-2 w-full py-2 text-xs text-slate-400 transition-colors hover:text-slate-600"
                        >
                          Zmień adres e-mail
                        </button>
                      </motion.div>
                    )}
                  </motion.div>
                )}

                {mode === "admin" && (
                  <motion.div
                    key="admin"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25 }}
                  >
                    <form onSubmit={handleAdminLogin} className="space-y-5">
                      <InputField
                        label="E-mail"
                        type="email"
                        value={adminEmail}
                        onChange={(e) => setAdminEmail(e.target.value)}
                        placeholder="admin@email.pl"
                        required
                        autoComplete="email"
                        icon={IconMail}
                      />

                      <InputField
                        label="Hasło"
                        type="password"
                        value={adminPassword}
                        onChange={(e) => setAdminPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        autoComplete="current-password"
                        icon={IconLock}
                      />

                      <RememberMeCheckbox checked={rememberMe} onChange={setRememberMe} />

                      {adminError && (
                        <motion.div
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                        >
                          {adminError}
                        </motion.div>
                      )}

                      <button
                        type="submit"
                        disabled={adminLoading}
                        className={`flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-slate-700 to-slate-800 px-5 py-3.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(15,23,42,0.18)] transition-all duration-200 ${
                          adminLoading
                            ? "cursor-not-allowed opacity-60"
                            : "hover:-translate-y-0.5 hover:shadow-[0_14px_28px_rgba(15,23,42,0.24)]"
                        }`}
                      >
                        {adminLoading ? (
                          <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                        ) : null}
                        {adminLoading ? "Logowanie..." : "Zaloguj się"}
                      </button>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="mt-6 text-center">
                <p className="text-xs text-slate-400">
                  Dostęp tylko dla uprawnionych użytkowników klasy
                </p>
                <p className="mt-2 text-xs text-slate-400">
                  Korzystając ze strony, akceptujesz{" "}
                  <Link
                    to="/privacy"
                    className="font-medium text-emerald-700 transition-colors hover:text-emerald-800"
                  >
                    politykę prywatności
                  </Link>
                  .
                </p>
                <p className="mt-3 text-xs text-slate-400">
                  Projekt i realizacja:{" "}
                  <a
                    href="https://rbaio.eu"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-slate-500 transition-colors hover:text-emerald-700"
                  >
                    rbaio.eu
                  </a>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
